/*
 * Copyright 2024 The Kokabo Authors. All rights reserved.
 * Use of this source code is governed by a BSD-style
 * license that can be found in the LICENSE file.
 */

import {Log} from "../utils/Log";

const strSpace = " ";
const NoEncoding = "";
const Base64Encoding = "base64";
const Fragment = "F_";

// Protocol payload limit: fragmentation keeps frames within reasonable bounds for transports and server parsers.
const MaxExecutionMessagePayloadSize = 32 * 1024;

/**
 * Protocol frame container for calls/answers/errors.
 *
 * This library is client-only: execution frames must be fully described within the message itself
 * (no implicit input reference), and fragmentation must be handled client-side for large payloads.
 */
class ExecutionMessage {

    /**
     * @param {string} sender
     * @param {string} recipient
     * @param {string} instruction
     * @param {string} callId
     * @param {string} callName
     * @param {string|Uint8Array} payload
     */
    constructor(sender, recipient, instruction, callId, callName, payload) {
        this.sender = sender;
        this.recipient = recipient;
        this.instruction = instruction;
        this.callId = callId;
        this.callName = callName;
        this.payload = payload;
    }

    // Chunking is a protocol constraint (not a transport optimization): oversized frames must be fragmented to stay interoperable.
    needsToBeChunked() {
        return this.payload.length > MaxExecutionMessagePayloadSize;
    }

    // Used by the Assembler to preserve headers without retaining large payload fragments.
    prototypeWithoutPayload() {
        return new ExecutionMessage(this.sender, this.recipient, this.instruction, this.callId, this.callName, "");
    }

    static fromJson(jsonString) {
        const obj = JSON.parse(jsonString);
        return new ExecutionMessage(obj.sender, obj.recipient, obj.instruction, obj.callId, obj.callName, obj.payload);
    }

    toJson() {
        return JSON.stringify(this);
    }

    /**
     * Kokabo textual protocol format: `sender:instruction callId callName payload:recipient`.
     * @return {string}
     */
    message() {
        return `${this.sender}:${this.instruction}${strSpace}${this.callId}${strSpace}${this.callName}${strSpace}${this.payload}:${this.recipient}`
    }

    /**
     * Fragmentation is applied transparently so transports only need to provide a reliable send function.
     * @param {function(string): Promise<void>} sendFunc
     * @param {string} [encoding=NoEncoding]
     * @param {number} [timeOut=5000]
     * @return {Promise<void>}
     */
    async transform(sendFunc, encoding = NoEncoding, timeOut = 5000) {
        if (this.needsToBeChunked()) {
            return await this._fragment(sendFunc, encoding, timeOut)
        } else {
            return await this._send(this.message(), sendFunc, encoding, timeOut)
        }
    }

    /**
     * Fragment metadata is embedded in callName to match the reference Go implementation and keep decoding stateless.
     * @param {function(string): Promise<void>} sendFunc
     * @param {string} encoding
     * @param {number} [timeOut=5000]
     * @return {Promise<void>}
     * @private
     */
    async _fragment(sendFunc, encoding = NoEncoding, timeOut = 5000) {
        const totalSize = this.payload.length;
        const totalChunks = Math.ceil(totalSize / MaxExecutionMessagePayloadSize);

        for (let i = 0; i < totalChunks; i++) {

            const start = i * MaxExecutionMessagePayloadSize;
            const end = Math.min(start + MaxExecutionMessagePayloadSize, totalSize);
            const chunk = this.payload.slice(start, end);
            const encodedChunk = encoding === Base64Encoding ? btoa(chunk) : chunk;

            /**
             * Metadata schema mirrors the server side and is intentionally compact:
             * ```go
             * type Metadata struct {
             *    Encoding  string `json:"e"`
             *    FileMode  bool   `json:"f"`
             *    CallName  string `json:"c"`
             *    Size      int    `json:"s"`
             *    Index     int    `json:"x"`
             *    Nb        int    `json:"n"`
             *    Last      bool   `json:"l"`
             *    Interrupt bool   `json:"i"`
             * }
             *```
             * @type {{e: string, f: boolean, c:string, s:number, x:number, n:number, l:boolean, i:boolean}}
             */
            const metadata = {
                e: encoding, f: (encoding === Base64Encoding), c: this.callName, s: totalSize, x: i, n: totalChunks, l: (i === totalChunks - 1), i: false,
            };
            const jMeta = JSON.stringify(metadata);
            const callName = `${Fragment}${btoa(jMeta)}`;
            const message = `${this.sender}:${this.instruction}${strSpace}${this.callId}${strSpace}${callName}${strSpace}${encodedChunk}:${this.recipient}`;
            await this._send(message, sendFunc, encoding, timeOut);
        }
    }

    /**
     * @param message
     * @param sendFunc
     * @param encoding
     * @param timeOut
     * @return {Promise<void>}
     * @private
     */
    async _send(message, sendFunc, encoding = NoEncoding, timeOut = 5000) {
        if (typeof sendFunc !== 'function') {
            throw new Error("sendFunc is not a function");
        }
        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Timeout exceeded")), timeOut)
        );
        try {
            await Promise.race([sendFunc(message), timeoutPromise]);
        } catch (error) {
            Log.error(`[ExecutionMessage._send] Error sending fragment: ${error.message}`);
            throw error;
        }
    }
}


export {ExecutionMessage, Fragment, MaxExecutionMessagePayloadSize, NoEncoding, Base64Encoding};